import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  ChevronDownIcon, 
  SparklesIcon, 
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  UsersIcon,
  EnvelopeIcon,
  PhoneIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { usePerformanceMonitor } from '../utils/performance';
import HeroSection from './HeroSection';
import MissionSection from './MissionSection';
import EliteStatsSection from './EliteStatsSection';
import CoreValues from './CoreValues';
import RentalProfitsSection from './RentalProfitsSection';
import TokenizedInvestments from './TokenizedInvestments';
import NFTMinting from './NFTMinting';
import UpcomingProjects from './UpcomingProjects';
import ProfitCalculator from './ProfitCalculator';
import TeamSection from './TeamSection';
import SocialMediaSection from './SocialMediaSection';
import ContactSection from './ContactSection';

const LandingPage = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  // Monitor performance
  usePerformanceMonitor();

  const sections = [
    { id: 'mission', label: 'Mission' },
    { id: 'investments', label: 'Investments' },
    { id: 'minting', label: 'Mint NFT' },
    { id: 'projects', label: 'Projects' },
    { id: 'calculator', label: 'Calculator' },
    { id: 'team', label: 'Team' },
    { id: 'social', label: 'Community' },
    { id: 'contact', label: 'Contact' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;
      
      sections.forEach(section => {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false); // Close mobile menu after navigation
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white overflow-x-hidden">
      {/* Navigation */}
      <motion.nav 
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-gray-800/30"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container mx-auto px-4 sm:px-6 py-1">
          <div className="flex items-center justify-between">
            <motion.div 
              className="cursor-pointer"
              whileHover={{ scale: 1.05 }}
              onClick={() => scrollToSection('hero')}
            >
              <img 
                src="/logo.png" 
                alt="Octagon Infinity" 
                className="h-16 sm:h-20 w-auto"
              />
            </motion.div>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex space-x-4 xl:space-x-6">
              {sections.map((section) => (
                <motion.button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`px-2 xl:px-3 py-2 rounded-lg transition-all duration-300 text-xs xl:text-sm ${
                    activeSection === section.id 
                      ? 'bg-amber-600 text-white' 
                      : 'text-gray-300 hover:text-white hover:bg-gray-800'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {section.label}
                </motion.button>
              ))}
            </div>

            {/* Mobile Menu Toggle */}
            <div className="lg:hidden">
              <motion.button
                className="p-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-800 transition-colors duration-200"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                whileTap={{ scale: 0.95 }}
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="w-6 h-6" />
                ) : (
                  <Bars3Icon className="w-6 h-6" />
                )}
              </motion.button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          <motion.div
            className={`lg:hidden overflow-hidden ${isMobileMenuOpen ? 'max-h-96' : 'max-h-0'}`}
            initial={false}
            animate={{ 
              maxHeight: isMobileMenuOpen ? 400 : 0,
              opacity: isMobileMenuOpen ? 1 : 0
            }}
            transition={{ duration: 0.3 }}
          >
            <div className="pt-4 pb-2 space-y-2">
              {sections.map((section) => (
                <motion.button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-300 ${
                    activeSection === section.id 
                      ? 'bg-amber-600 text-white' 
                      : 'text-gray-300 hover:text-white hover:bg-gray-800'
                  }`}
                  whileHover={{ x: 5 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {section.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.nav>

      {/* Sections */}
      <HeroSection />
      <MissionSection />
      <CoreValues />
      <RentalProfitsSection />
      <TokenizedInvestments />
      <EliteStatsSection />
      <NFTMinting />
      <ProfitCalculator />
      <UpcomingProjects />
      <TeamSection />
      <SocialMediaSection />
      <ContactSection />

      {/* Scroll to top button */}
      <motion.button
        className="fixed bottom-8 right-8 bg-amber-600 hover:bg-amber-700 text-white p-4 rounded-full shadow-lg hover:shadow-amber-500/25 transition-all duration-300"
        onClick={() => scrollToSection('hero')}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        style={{ opacity }}
      >
        <ChevronDownIcon className="w-6 h-6 rotate-180" />
      </motion.button>
    </div>
  );
};

export default LandingPage;